import { useState } from "react";
import { MDX, ALPHA_B_METRICS, SIGMA_B_METRICS } from "./mdx/tokens";
import { X } from "lucide-react";
import { Reveal } from "@/components/Reveal";

/**
 * MetricsMatrix — Section 3.
 * Interactive tabs for Behavioral Alpha (α_B) and Behavioral Sigma (σ_B).
 * Clicking any card opens the MetricDrawer with formula/params/countermeasures.
 */
export default function MetricsMatrix({ isDark }) {
    const [tab, setTab] = useState("alpha");
    const [drawer, setDrawer] = useState(null);
    const items = tab === "alpha" ? ALPHA_B_METRICS : SIGMA_B_METRICS;

    const bgSection = isDark ? "bg-[#090D16]" : "bg-white";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const cardBg = isDark ? "bg-[#0F1524]" : "bg-white";
    const cardBrd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";

    return (
        <section className={`${bgSection} ${text} py-16 md:py-24`} data-testid="metrics-matrix">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                <div className="max-w-3xl mb-10">
                    <div className="font-inter text-xs font-bold uppercase tracking-widest mb-3" style={{ color: MDX.blueLow }}>
                        Your Trading Habits, Tracked
                    </div>
                    <h2 className="font-inter font-medium leading-tight" style={{ fontSize: "28px" }}>
                        Ten habits that make or break new traders. We watch all of them for you.
                    </h2>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6" data-testid="metrics-tabs">
                    {[
                        { key: "alpha", label: "What's Working" },
                        { key: "sigma", label: "What To Watch" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`metrics-tab-${t.key}`}
                            className={`font-inter font-bold uppercase tracking-wider text-[13px] px-4 py-2 rounded-md border transition-colors ${
                                tab === t.key
                                    ? "text-white border-transparent"
                                    : `${dim} ${cardBrd} hover:border-[${MDX.blueLow}]`
                            }`}
                            style={tab === t.key ? { background: MDX.blueLowSolid } : {}}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Metric cards grid — fades in one card at a time */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="metrics-grid">
                    {items.map((m, i) => (
                        <Reveal key={m.id} delay={i * 0.06} className="h-full">
                            <button
                                onClick={() => setDrawer(m)}
                                data-testid={`metric-card-${m.id}`}
                                className={`w-full h-full text-left rounded-lg border p-4 transition-all hover:-translate-y-0.5 hover:border-[${MDX.blueLow}] ${cardBg} ${cardBrd}`}
                            >
                                <div className="font-mono text-[11px] font-bold mb-1" style={{ color: MDX.blueLow }}>{m.key}</div>
                                <div className="font-inter font-bold text-[15px] leading-tight mb-2">{m.name}</div>
                                <div className={`font-roboto text-[12px] font-semibold uppercase tracking-wider mb-1`} style={{ color: MDX.orangeHi }}>{m.principle}</div>
                                <div className={`font-roboto text-[13px] leading-snug ${dim}`}>Tap to see how the platform helps</div>
                            </button>
                        </Reveal>
                    ))}
                </div>
            </div>

            {drawer && <MetricDrawer metric={drawer} isDark={isDark} onClose={() => setDrawer(null)} />}
        </section>
    );
}

/* ── Drawer ─────────────────────────────────────────────── */
function MetricDrawer({ metric, isDark, onClose }) {
    const bg = isDark ? "bg-[#0F1524]" : "bg-white";
    const brd = isDark ? "border-[#1F2937]" : "border-[#E5E7EB]";
    const text = isDark ? "text-[#F9FAFB]" : "text-[#090D16]";
    const dim = isDark ? "text-[#9CA3AF]" : "text-[#6B7280]";
    return (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="metric-drawer">
            <div
                className={`w-full sm:max-w-lg ${bg} ${text} border ${brd} rounded-t-2xl sm:rounded-2xl p-6 shadow-2xl`}
                onClick={(e) => e.stopPropagation()}
                data-testid="metric-drawer-body"
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="font-mono text-xs font-bold mb-1" style={{ color: MDX.blueLow }}>{metric.key}</div>
                        <div className="font-inter font-bold text-xl leading-tight">{metric.name}</div>
                    </div>
                    <button onClick={onClose} data-testid="metric-drawer-close" aria-label="Close" className={`p-2 rounded-md border ${brd}`}>
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <Section label="The habit">
                    <p className="font-inter font-bold text-[15px] leading-tight" style={{ color: MDX.orangeHi }}>{metric.principle}</p>
                </Section>
                <Section label="What this checks">
                    <p className={`font-roboto text-[15px] leading-relaxed ${dim}`}>{metric.explains}</p>
                </Section>
                <Section label="How we help">
                    <p className="font-roboto text-[15px] leading-relaxed" style={{ color: MDX.blueLow }}>{metric.counter}</p>
                </Section>
            </div>
        </div>
    );
}
function Section({ label, children }) {
    return (
        <div className="mb-4">
            <div className="font-inter text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color: MDX.blueLow }}>{label}</div>
            {children}
        </div>
    );
}
